import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { CheckCircle2, AlertCircle } from "lucide-react";
import {
  bulkMarkPaid,
  type BulkOperationResultView,
} from "../../../api/General/Subscription";
import { addDays, toInputDate } from "./helpers";
import { getApiErrorMessage } from '../../../utils/apiErrors';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  companyIds: number[];
  onSuccess: (result: BulkOperationResultView) => void;
}

export default function BulkMarkPaidDialog({
  open,
  onOpenChange,
  companyIds,
  onSuccess,
}: Props) {
  const { t } = useTranslation();

  const defaultDate = useMemo(() => toInputDate(addDays(new Date(), 365)), []);
  const [paidUntil, setPaidUntil] = useState<string>(defaultDate);
  const [notes, setNotes] = useState<string>("");
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setPaidUntil(defaultDate);
      setNotes("");
      setError(null);
      setSubmitting(false);
    }
  }, [open, defaultDate]);

  const count = companyIds.length;

  // CO-38 — A REASON IS MANDATORY, exactly as it is on every override in the Platform Billing
  // console. This dialog moves money-adjacent state on up to N companies at once and was the only
  // operator override in the product that asked for nothing at all; six months later an unexplained
  // bulk mark-paid is indistinguishable from a mistake.
  const reasonMissing = notes.trim().length === 0;

  /**
   * 🔴 VALIDATE BEFORE SUBMIT — same three failures the single-company dialog had, multiplied by N.
   *
   * Clearing the date input leaves `paidUntil === ""`, and `new Date("").toISOString()` throws a
   * RangeError that lands in the submit `catch` and is reported as a server problem, for a mistake
   * the browser could see without sending anything. A past date went through and marked every
   * selected company Paid until a date already gone, i.e. instantly lapsed, silently.
   *
   * Returns the sentence to show, or null when the input is submittable.
   *
   * 🔴 THE "INDEFINITE (NO EXPIRY)" CHECKBOX WAS REMOVED, AND MUST NOT COME BACK. It submitted
   * `paidUntil: null`, which the server reads as PERPETUAL entitlement — the same state the
   * dedicated never-expires action grants, but with no reason captured and no audit row, and here
   * for N companies in a single call. `BulkMarkPaidAsync` now rejects a null `paidUntil` outright,
   * so a re-added checkbox would only produce a 400. Perpetual entitlement has exactly one route:
   * the Platform Billing never-expires action, linked from the copy below.
   */
  const validationError = (): string | null => {
    const parsed = paidUntil ? new Date(paidUntil) : null;
    if (!parsed || Number.isNaN(parsed.getTime())) {
      return t("Choose a paid until date.");
    }
    // Compared as `yyyy-mm-dd` STRINGS, which sort correctly and are timezone-free. Re-reading the
    // parsed Date with local getters would shift the day by one west of UTC, because a bare
    // `yyyy-mm-dd` parses as UTC midnight.
    if (paidUntil <= toInputDate(new Date())) {
      return t("The paid until date must be later than today.");
    }
    if (reasonMissing) {
      return t("A reason is required.");
    }
    return null;
  };

  const submit = async () => {
    if (count === 0) return;
    const invalid = validationError();
    if (invalid) {
      setError(invalid);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const result = await bulkMarkPaid({
        companyIds,
        paidUntil: new Date(paidUntil).toISOString(),
        // Never `|| null` — the server rejects a blank Notes, and validationError() has already
        // guaranteed this is non-empty by the time we get here.
        notes: notes.trim(),
      });
      if (result.errors && result.errors.length > 0) {
        setError(getApiErrorMessage(result, t, t("Failed to update subscription")));
        setSubmitting(false);
        return;
      }
      if (result.payload) {
        onSuccess(result.payload);
      }
      onOpenChange(false);
    } catch (e) {
      const message = getApiErrorMessage(e, t, t("Failed to update subscription"));
      setError(message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="default">
        <DialogHeader>
          <div className="flex items-start gap-3">
            <div className="shrink-0 w-9 h-9 rounded-lg bg-emerald-100 dark:bg-emerald-900/40 text-emerald-700 dark:text-emerald-300 flex items-center justify-center">
              <CheckCircle2 size={18} />
            </div>
            <div className="min-w-0">
              <DialogTitle>{t("Mark as Paid")}</DialogTitle>
              <DialogDescription className="mt-1">
                {t("{{count}} companies selected", { count })}
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <DialogBody className="space-y-4">
          <div className="rounded-md bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-900/50 p-3 text-sm text-emerald-800 dark:text-emerald-300">
            {t("This will mark {{count}} companies as Paid and clear any active trial.", {
              count,
            })}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="bmp-paid-until">
              {t("Paid Until Date")}
            </label>
            <Input
              id="bmp-paid-until"
              type="date"
              value={paidUntil}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                setPaidUntil(e.target.value);
                // Drop the validation sentence the moment the operator edits the field it accuses,
                // so the dialog never argues with what is on screen.
                setError(null);
              }}
            />
            {/* Replaces the removed "indefinite" checkbox: the affordance is gone, but the operator
                who wanted it still needs to be told where perpetual entitlement actually lives. */}
            <p className="text-xs text-muted-foreground">
              {t("Mark as Paid always needs an end date. Entitlement that never expires is a separate, audited action that records who granted it and why.")}{" "}
              <Link
                to="/settings/operator/subscriptions?tab=platform-billing"
                onClick={() => onOpenChange(false)}
                className="font-medium text-primary underline underline-offset-2 hover:no-underline"
              >
                {t("Grant never-expires in Platform Billing")}
              </Link>
            </p>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="bmp-notes">
              {t("Reason")} <span className="text-red-600 dark:text-red-400">*</span>
            </label>
            <textarea
              id="bmp-notes"
              rows={3}
              value={notes}
              onChange={(e) => {
                setNotes(e.target.value);
                // Same rule as the date field: stop accusing a field the operator is already fixing.
                setError(null);
              }}
              placeholder={t("Why are these companies being marked as paid?")}
              className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary transition-colors disabled:opacity-50"
            />
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-900/50 p-3 text-sm text-red-800 dark:text-red-300">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>
            {t("Cancel")}
          </Button>
          <Button
            onClick={submit}
            loading={submitting}
            variant="success"
            disabled={count === 0 || reasonMissing}
          >
            {t("Mark {{count}} as Paid", { count })}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
